/**
 * Processes HTML content to inject theme-aware styling
 * - Replaces all fonts with the application's main font
 * - Applies color filters to match the theme
 */

interface ProcessHtmlOptions {
  fontFamily: string
  backgroundColor: string
  textColor: string
  isDark: boolean
}

/**
 * Injects CSS into HTML to override fonts and apply theme colors
 */
export function processHtmlForTheme(
  htmlContent: string,
  options: ProcessHtmlOptions
): string {
  const { fontFamily, backgroundColor, textColor, isDark } = options

  // CSS to inject - uses !important to override any existing styles
  const styleInjection = `
    <style id="app-theme-override">
      /* Font override for all elements */
      * {
        font-family: ${fontFamily} !important;
      }

      /* Color theme application */
      body {
        background-color: ${backgroundColor} !important;
        color: ${textColor} !important;
      }

      /* Apply theme to common elements */
      p, div, span, h1, h2, h3, h4, h5, h6, li, td, th, label, input, textarea, select, button {
        color: inherit;
      }

      /* Adjust links for theme */
      a {
        color: ${isDark ? '#5B8EF4' : '#4169E1'} !important;
      }

      /* Adjust borders for theme */
      * {
        border-color: ${isDark ? '#404752' : '#E5E5E5'} !important;
      }

      /* Make sure text inputs work with theme */
      input, textarea, select {
        background-color: ${isDark ? '#232730' : '#FAFAFA'} !important;
        color: ${textColor} !important;
        border: 1px solid ${isDark ? '#404752' : '#E5E5E5'} !important;
      }

      /* Button styling */
      button {
        background-color: ${isDark ? '#404752' : '#E5E5E5'} !important;
        color: ${textColor} !important;
        border: 1px solid ${isDark ? '#404752' : '#E5E5E5'} !important;
      }

      button:hover {
        background-color: ${isDark ? '#4A5261' : '#D5D5D5'} !important;
      }
    </style>
  `

  // Check if HTML has a <head> tag
  if (htmlContent.includes('<head>')) {
    // Insert right after <head>
    return htmlContent.replace(/<head>/i, `<head>${styleInjection}`)
  } else if (htmlContent.includes('<html>')) {
    // Insert right after <html>
    return htmlContent.replace(
      /<html>/i,
      `<html><head>${styleInjection}</head>`
    )
  } else {
    // No html structure, wrap entire content
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${styleInjection}
</head>
<body>
  ${htmlContent}
</body>
</html>`
  }
}
